import { useFormContext } from 'react-hook-form'
import {
	useGetGoogleStatus,
	useGetOutlookStatus,
} from '@cocoonspace/sdk-js/v2/domains/auth/auth'
import { useListEventTypeCalendars } from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@cocoonspace/ui/components/collapsible'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@cocoonspace/ui/components/dropdown-menu'
import { useLingui } from '@lingui/react/macro'
import { ChevronDownIcon, PlusIcon } from 'lucide-react'

import { GoogleCalendarIcon } from '~/components/icons/google-calendar-icon'
import { OutlookIcon } from '~/components/icons/outlook-icon'
import { PageLoader } from '~/components/page-loader'
import {
	SectionTitle,
	SectionTitleBtn,
	SectionWrapper,
} from '~/components/sheet-section-wrapper'
import { IntegrationsList } from '~/domains/account/components/integrations/integrations-list'
import { useFormSectionCollapsible } from '../use-form-section-collapsible.hook'
import { CalendarList } from './calendar-list'

type CalendarRef = {
	id: string
	type: 'google' | 'outlook'
}

const CalendarPickerContent = ({
	value,
	onAdd,
}: {
	value: CalendarRef[]
	onAdd: (calendar: CalendarRef) => void
}) => {
	const { data: calendars } = useListEventTypeCalendars()

	return (
		<>
			{calendars
				?.filter((calendar) => !value.some((ref) => ref.id === calendar.id))
				.map((calendar) => (
					<DropdownMenuItem
						key={calendar.id}
						className='flex min-w-36 items-center gap-2 text-sm'
						onClick={() =>
							onAdd({
								id: calendar.id,
								type: calendar.provider,
							})
						}
					>
						<div
							className='bg-muted size-2 rounded-full'
							style={{ backgroundColor: calendar.color }}
						/>
						{calendar.provider === 'google' && (
							<GoogleCalendarIcon className='size-4' />
						)}
						{calendar.provider === 'outlook' && (
							<OutlookIcon className='size-4' />
						)}
						<span className='flex-1 truncate'>{calendar.name}</span>
					</DropdownMenuItem>
				))}
		</>
	)
}

const CalenderMenu = () => {
	const { t } = useLingui()
	const form = useFormContext()

	const { data: googleStatus } = useGetGoogleStatus()
	const { data: outlookStatus } = useGetOutlookStatus()

	if (!googleStatus?.connected && !outlookStatus?.connected) return null

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				className='ml-auto'
				asChild
			>
				<SectionTitleBtn>
					<PlusIcon />
					{t`Ajouter`}
				</SectionTitleBtn>
			</DropdownMenuTrigger>

			<DropdownMenuContent className='max-h-72 overflow-y-auto'>
				<CalendarPickerContent
					value={form.watch('conflictCalendarRefs')}
					onAdd={(calendar) => {
						form.setValue(
							'conflictCalendarRefs',
							[...form.watch('conflictCalendarRefs'), calendar],
							{ shouldDirty: true },
						)
					}}
				/>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export const CalendarSync = () => {
	const { t } = useLingui()
	const { onOpenChange, open } = useFormSectionCollapsible([
		'conflictCalendarRefs',
	])

	const {
		isLoading: googleLoading,
		isSuccess: googleSuccess,
		data: googleStatus,
	} = useGetGoogleStatus()
	const {
		isLoading: outlookLoading,
		isSuccess: outlookSuccess,
		data: outlookStatus,
	} = useGetOutlookStatus()

	const isLoading = googleLoading || outlookLoading
	const isReady = googleSuccess && outlookSuccess

	const googleConnected = googleStatus?.connected
	const outlookConnected = outlookStatus?.connected
	const hasConnected = googleConnected || outlookConnected

	if (isLoading) return <PageLoader className='mx-auto' />
	if (!isReady) return null

	return (
		<Collapsible
			open={open}
			onOpenChange={onOpenChange}
		>
			<SectionWrapper>
				<SectionTitle>
					<CollapsibleTrigger className='flex min-w-0 flex-1 items-center gap-2 text-left'>
						{t`Conflits calendriers`}
					</CollapsibleTrigger>

					<div className='flex items-center gap-2'>
						{hasConnected && <CalenderMenu />}
					</div>

					<CollapsibleTrigger
						aria-label={t`Conflits calendriers`}
						className='flex size-4 shrink-0 items-center [&[data-state=open]>svg]:rotate-180'
					>
						<ChevronDownIcon className='size-4 transition-transform' />
					</CollapsibleTrigger>
				</SectionTitle>

				<CollapsibleContent>
					<p className='bg-secondary mb-2 rounded p-2 text-xs'>
						{t`Choisissez les calendriers à vérifier pour éviter les doubles réservations`}
					</p>

					{!hasConnected && <IntegrationsList dense />}
					{hasConnected && <CalendarList />}
				</CollapsibleContent>
			</SectionWrapper>
		</Collapsible>
	)
}
